// ---------- peinture nuancée : la même touche, 256 couleurs, une vingtaine par tableau ----------
//   0         case vide, du papier nu (le fond relié au bord, cf. l'encodeur)
//   1..24     les vingt-quatre couleurs de `peinture`, mêmes codes, mêmes noms
//   25..256   deux cent trente-deux couleurs de plus, réparties dans tout l'espace des couleurs
//
// D'OÙ VIENT CET ALPHABET. « Touches de peinture » manquait de nuances (2026-10-17) : vingt-quatre
// couleurs fixes, réglées sur des tableaux anciens, et pour un chaton noir sur un canapé gris pas
// un gris de la bonne teinte — le canapé se couvrait de touches orange, bleues et violettes que
// la diffusion d'erreur mélangeait pour retrouver du gris. Ici la conversion CHOISIT les couleurs
// du tableau dans un grand nuancier : le canapé reçoit ses gris, le chat ses noirs.
//
// Mesuré sur treize images (le chaton, sept images générées, cinq tableaux et monuments ; 120
// cases, écart OKLab ×100 à l'original, toile et original floutés d'une case, fond exclu) :
//
//   peinture actuelle, 24 couleurs             10,36
//   les mêmes 24, sur l'image sans retouche     6,60
//   256 couleurs, 20 choisies par image         5,42
//   1 024 → 20 : 5,27    4 096 → 20 : 5,24     — le plafond est atteint, 256 suffit
//
// Trente couleurs par image au lieu de vingt ne gagnaient rien (5,81 contre 5,82) ; réserver deux
// des vingt au plus sombre et au plus clair de l'image (le k-means tire ses centres vers
// l'intérieur) perdait même un peu (5,82 contre 5,42).
// Le banc : https://claude.ai/artifact/SjNVoE5iuX4k8tNErhnrJa
//
// UN ALPHABET À PART ET NON `peinture` ÉLARGI. Un client qui ne connaît pas ces codes ne
// recevrait de `peinture` que des cases qu'il ne sait pas peindre ; un identifiant neuf, lui, se
// déclare au serveur (knownAlphabetIds), qui ne l'envoie qu'aux versions qui savent le dessiner.
// Et les grilles de `peinture` gardent leurs codes : seul l'élargissement de la touche (0,80, le
// même jour) a touché à leur rendu.
//
// Tout le dessin vient de `peinture.ts` — la touche, son orientation calculée depuis la grille,
// la pastille, la mesure de ce que rend chaque couleur. Seuls changent le nuancier et la
// conversion (peintureNuancee.encode.ts).

import {
  PAINTS,
  angleField,
  hexRgb,
  measureTones,
  paintSwatch,
  srgbToOklab,
  stroke,
  toneOf,
} from './peinture';
import type { Alphabet } from './types';

/**
 * Les 232 couleurs ajoutées, codes 25 à 256, dans l'ordre où elles ont été tirées.
 *
 * Tirées une fois, et ÉCRITES ici plutôt que recalculées au chargement : un code garde son sens
 * pour toujours, et un calcul retouché un jour changerait en silence la couleur de chaque case
 * des grilles publiées. La recette, pour mémoire : parmi les 4 096 couleurs d'une grille sRGB de
 * 16 niveaux par canal, prendre à chaque tour celle qui est la plus loin de toutes les couleurs
 * déjà prises — en partant des vingt-quatre de `peinture` —, avec la distance de l'encodeur
 * (clarté², plus 1,15 × l'écart de teinte²). Le nuancier couvre ainsi l'espace le plus
 * régulièrement possible, sans préférer aucune région.
 */
const MORE: readonly string[] = [
  '#00ff00',
  '#cc00ff',
  '#ff88ff',
  '#0000ff',
  '#00bb00',
  '#ffff00',
  '#000000',
  '#00ffcc',
  '#7744ff',
  '#000099',
  '#aa77dd',
  '#ff00bb',
  '#aa0077',
  '#0088ff',
  '#000044',
  '#99dd55',
  '#00cc99',
  '#5500cc',
  '#00ddff',
  '#ff88aa',
  '#550066',
  '#99aa33',
  '#9900bb',
  '#eebbff',
  '#ff3300',
  '#ccffaa',
  '#886699',
  '#330033',
  '#aa6600',
  '#5588bb',
  '#440000',
  '#2255ee',
  '#bb44bb',
  '#ff44ff',
  '#333399',
  '#bb99cc',
  '#007777',
  '#88ffff',
  '#888833',
  '#005577',
  '#009900',
  '#dd66bb',
  '#000011',
  '#994466',
  '#aaff44',
  '#006600',
  '#220066',
  '#99ddaa',
  '#ff0077',
  '#8855cc',
  '#aa55ff',
  '#00bbcc',
  '#ffdd55',
  '#11dd22',
  '#aa6666',
  '#ddaaaa',
  '#7777ff',
  '#ff9955',
  '#660099',
  '#770055',
  '#990033',
  '#ff6666',
  '#775500',
  '#33ff88',
  '#1133cc',
  '#553355',
  '#5511ff',
  '#003300',
  '#cc9900',
  '#00aa66',
  '#665577',
  '#550033',
  '#880088',
  '#5544bb',
  '#9911ee',
  '#cc99ff',
  '#bb7799',
  '#110000',
  '#cc0066',
  '#994499',
  '#aabbff',
  '#ddddaa',
  '#336644',
  '#dd2299',
  '#dd66ee',
  '#110022',
  '#ffbbcc',
  '#440088',
  '#77aa77',
  '#8899bb',
  '#dd00cc',
  '#9999ff',
  '#7722cc',
  '#22ddcc',
  '#663300',
  '#ee99dd',
  '#ff5599',
  '#5566dd',
  '#bbbb66',
  '#ee0044',
  '#003377',
  '#cc5511',
  '#33dd77',
  '#ff77cc',
  '#7777cc',
  '#773366',
  '#444477',
  '#ffddff',
  '#ccee66',
  '#ffffbb',
  '#002233',
  '#cccc00',
  '#00aaff',
  '#445500',
  '#ee8877',
  '#004455',
  '#55bb55',
  '#99ddff',
  '#669922',
  '#bb00cc',
  '#995533',
  '#88cc77',
  '#222200',
  '#3300aa',
  '#777788',
  '#bb5599',
  '#2277cc',
  '#77ccdd',
  '#447799',
  '#77ee99',
  '#ccffdd',
  '#558866',
  '#77cc22',
  '#77ee33',
  '#cc7766',
  '#66aaaa',
  '#ddaa77',
  '#997755',
  '#220011',
  '#662277',
  '#aa1100',
  '#ff6611',
  '#881100',
  '#775544',
  '#bb2299',
  '#446677',
  '#ccccff',
  '#6699ff',
  '#dd44ff',
  '#221144',
  '#332266',
  '#003333',
  '#eeccbb',
  '#55eeee',
  '#dd88bb',
  '#aa9999',
  '#000077',
  '#ff55cc',
  '#9966bb',
  '#ddcc77',
  '#99ffcc',
  '#6666aa',
  '#442244',
  '#0099dd',
  '#dd5555',
  '#664455',
  '#77ccaa',
  '#9988cc',
  '#aaaa77',
  '#552211',
  '#aa2255',
  '#001100',
  '#225555',
  '#0011bb',
  '#bb8833',
  '#4433dd',
  '#8833aa',
  '#bb4466',
  '#005522',
  '#aa4444',
  '#999955',
  '#556600',
  '#7700ff',
  '#ffff88',
  '#aabb00',
  '#6644dd',
  '#ffaa99',
  '#777700',
  '#bb77ff',
  '#444455',
  '#dd44bb',
  '#5522aa',
  '#bbccaa',
  '#5555ff',
  '#004400',
  '#aa44dd',
  '#554400',
  '#dd7788',
  '#ee9900',
  '#008899',
  '#ee00ee',
  '#cc44dd',
  '#331122',
  '#0044aa',
  '#99ff99',
  '#886622',
  '#bbdd77',
  '#ddbbdd',
  '#99bbdd',
  '#ff4466',
  '#555599',
  '#ee44dd',
  '#bbaabb',
  '#443300',
  '#aaeedd',
  '#bb66bb',
  '#997799',
  '#cc88dd',
  '#0055cc',
  '#33bbee',
  '#eeee99',
  '#114488',
  '#001122',
  '#ccaaee',
  '#660000',
  '#44aa22',
  '#0000dd',
  '#227700',
  '#557777',
  '#553333',
  '#55bb99',
  '#883344',
];

/** Le nuancier complet, indice = code − 1. */
export const NUANCES: readonly string[] = [...PAINTS.map((p) => p.css), ...MORE];

const TONES = NUANCES.map(toneOf);

/** Clarté et teinte OKLab de chaque code (indice = code ; 0, le papier, compte pour du blanc). */
const LAB = NUANCES.map((css) => srgbToOklab(...hexRgb(css)));
const CODE_L = [1, ...LAB.map((l) => l[0])];

/**
 * Le nom d'une couleur, pour l'infobulle de sa pastille. Les vingt-quatre de `peinture` gardent
 * le leur ; les autres reçoivent un nom DÉCRIT — teinte, puis clarté — plutôt que deux cent
 * trente-deux noms de pigments inventés qu'aucun joueur ne saurait relier à une couleur.
 */
function describe(code: number): string {
  if (code <= PAINTS.length) return PAINTS[code - 1]?.name ?? '';
  const lab = LAB[code - 1];
  return lab ? describeOklab(lab) : '';
}

/** Le nom DÉCRIT d'une couleur OKLab — teinte, puis clarté. Exporté pour `briquesNuancees`, qui
 *  nomme ainsi ses couleurs au-delà des vingt-trois briques de base. */
export function describeOklab([L, a, b]: readonly [number, number, number]): string {
  const C = Math.hypot(a, b);
  if (C < 0.035) {
    if (L > 0.92) return 'blanc';
    if (L < 0.25) return 'noir';
    return L > 0.75 ? 'gris clair' : L < 0.45 ? 'gris sombre' : 'gris';
  }
  const h = ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  // Les angles de teinte OKLab : rouge ~29°, orange ~55°, jaune ~110°, vert ~142°, bleu ~264°.
  let hue =
    h < 15 || h >= 345
      ? 'rose'
      : h < 45
        ? 'rouge'
        : h < 75
          ? 'orange'
          : h < 120
            ? 'jaune'
            : h < 170
              ? 'vert'
              : h < 220
                ? 'turquoise'
                : h < 285
                  ? 'bleu'
                  : h < 320
                    ? 'violet'
                    : 'rose';
  // un orange ou un jaune sombre se dit brun, un rouge sombre bordeaux
  if (L < 0.5 && (hue === 'orange' || hue === 'jaune')) hue = 'brun';
  else if (L < 0.45 && hue === 'rouge') hue = 'bordeaux';
  const tint = C < 0.07 ? ' grisé' : '';
  const value =
    L < 0.35 ? ' profond' : L < 0.55 ? ' sombre' : L > 0.85 ? ' pâle' : L > 0.72 ? ' clair' : '';
  return hue + tint + value;
}

/** Les blancs, posés en dernier (cf. paletteOrder) : très clairs ET sans couleur. Le critère
 *  retrouve exactement les deux blancs de `peinture` ; un jaune vif, aussi clair qu'eux, n'en est
 *  pas un. */
const isWhite = (code: number) => {
  const [L, a, b] = LAB[code - 1];
  return L >= 0.9 && Math.hypot(a, b) < 0.04;
};

const angles = angleField(CODE_L);

export const peintureNuancee: Alphabet = {
  id: 'peintureNuancee',
  label: 'Peinture nuancée',
  symbolName: 'couleur',
  selfColored: true,
  helpText:
    'Une touche de pinceau par case, comme « Touches de peinture », mais les couleurs sont ' +
    'choisies pour chaque tableau parmi deux cent cinquante-six : une vingtaine, les plus justes.',

  aspect: () => 1,
  sheetMode: 'single',

  // La règle de `peinture`, et pour les mêmes raisons mesurées (cf. son paletteOrder) : du plus
  // clair au plus sombre, les sombres par-dessus, puis les blancs en tout dernier — les rehauts.
  // La palette ne liste que l'ordre ; la rangée de pastilles n'en montre que les couleurs de la
  // feuille (cf. buildLegend dans src/ui.ts).
  paletteOrder: (() => {
    const byLightness = NUANCES.map((_, i) => i + 1).sort((a, b) => CODE_L[b] - CODE_L[a]);
    const whites = byLightness.filter(isWhite);
    return [...byLightness.filter((c) => !isWhite(c)), ...whites.reverse()];
  })(),
  orderedPlay: true,
  bareSheet: true,

  codeName: describe,

  prepareGrid(target, originX = 0, originY = 0) {
    angles.prepare(target, originX, originY);
  },

  draw(ctx, x, y, w, h, code) {
    if (code < 1 || code > NUANCES.length) return;
    stroke(ctx, x, y, w, h, TONES[code - 1], angles.at(x, y, w, h));
  },

  swatch(ctx, w, h, code) {
    const t = TONES[code - 1];
    if (t) paintSwatch(ctx, w, h, t);
  },
};

/**
 * Ce que rend vraiment chaque code, en OKLab — la mesure de `peinture`, sur 256 couleurs. Une
 * fois par session.
 *
 * D'un seul tenant, elle prend 0,67 s mesuré sur un processeur ralenti ×4 (un téléphone moyen) :
 * dans « Créer », le premier aperçu de cette technique figeait l'écran autant. D'où `warmNuances`,
 * que l'outil lance dès son ouverture : la mesure se fait par paquets, pendant que le joueur
 * choisit et cadre sa photo. Une conversion demandée avant la fin finit ici, d'un coup, ce qui
 * reste — la même mesure, couleur par couleur, dans le même ordre.
 */
const measured: [number, number, number][] = [];
export function measuredNuances(): [number, number, number][] {
  if (measured.length < TONES.length) measured.push(...measureTones(TONES.slice(measured.length)));
  return measured;
}

/** La mesure par paquets de seize couleurs, un paquet par tâche : quelques dizaines de
 *  millisecondes chacun, que le fil principal sépare des gestes du joueur. */
let warming: Promise<void> | null = null;
export function warmNuances(): Promise<void> {
  warming ??= (async () => {
    while (measured.length < TONES.length) {
      await new Promise((r) => setTimeout(r, 0));
      const n = measured.length;
      measured.push(...measureTones(TONES.slice(n, n + 16)));
    }
  })();
  return warming;
}
